import { useMemo, useState, useEffect } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useInvestments, useCreateInvestment, useUpdateInvestment, useDeleteInvestment, type InvestmentAccount, type InvestmentFormData } from '@/hooks/useInvestments';
import { useInvestmentTypes } from '@/hooks/useInvestmentTypes';
import { useFetchAccounts } from '@accountHooks';
import { useRiskRatings } from '@/hooks/useRiskRatings';
import { useInvestmentCurrencyConversion } from '@/hooks/useInvestmentCurrencyConversion';
import { useCreateInvestmentTransaction } from '@/hooks/useInvestmentTransactions';
import type { InvestmentTransactionData } from '@/types/investments';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TypeSummaryCard } from '@/components/investment-summary/TypeSummaryCard';
import { InvestmentDistributionChart } from '@/components/investment-charts/InvestmentDistributionChart';
import { InvestmentCard } from '@/components/investment-list/InvestmentCard';
import { InvestmentForm } from '@/components/investment-form/InvestmentForm';
import { InvestmentTransactionForm } from '@/components/investment-transaction-form/InvestmentTransactionForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  PageHeaderSkeleton,
  PortfolioSummarySkeleton,
  DistributionChartSkeleton,
  AccountCardSkeleton,
} from '@/components/LoadingSkeletons';
import { toast } from 'sonner';

/**
 * BalancedFundsPage component displays and manages Balanced Fund investments.
 * This is a dedicated page for Balanced Funds accessible at /investments/balanced-funds.
 */
export default function BalancedFundsPage() {
  // Dialog state management
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [investmentFormOpen, setInvestmentFormOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<InvestmentAccount | null>(null);
  const [deletingInvestmentId, setDeletingInvestmentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch data using hooks
  const { data: investments = [], isLoading: isLoadingInvestments, error: investmentsError } = useInvestments();
  const { data: investmentTypes = [], isLoading: isLoadingTypes, error: typesError } = useInvestmentTypes();
  const { data: riskRatings = [], isError: riskRatingsError } = useRiskRatings();
  const { data: accounts = [] } = useFetchAccounts();

  // Mutation hooks for create and update
  const createInvestment = useCreateInvestment();
  const updateInvestment = useUpdateInvestment();
  const deleteInvestment = useDeleteInvestment();
  const createInvestmentTransaction = useCreateInvestmentTransaction();

  // Find Balanced Fund type ID from investment types
  const balancedFundTypeId = useMemo(() => {
    const balancedFundType = investmentTypes.find(
      (type) => type.name.toLowerCase() === 'balanced fund'
    );
    return balancedFundType?.id;
  }, [investmentTypes]);

  // Filter investments by Balanced Fund type
  const balancedFunds = useMemo(() => {
    if (!balancedFundTypeId) return [];
    let filtered = investments.filter(
      (investment) => investment.investment_type_id === balancedFundTypeId
    );
    
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(inv => 
        inv.fund_name?.toLowerCase().includes(lowerQuery) ||
        inv.fund_manager?.toLowerCase().includes(lowerQuery) ||
        inv.account_number?.toLowerCase().includes(lowerQuery) ||
        inv.member_number?.toLowerCase().includes(lowerQuery)
      );
    }
    
    return filtered;
  }, [investments, balancedFundTypeId, searchQuery]);

  // Determine base currency (first investment or USD default)
  const baseCurrency = useMemo(() => {
    if (balancedFunds.length > 0) {
      return balancedFunds[0].currency;
    }
    return 'USD';
  }, [balancedFunds]);

  // Currency conversion integration
  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedInvestments,
    totalNetWorth,
    isConverting,
    conversionError,
  } = useInvestmentCurrencyConversion({
    baseCurrency,
    investments: balancedFunds,
  });

  // Handler to open deposit transaction form
  const handleOpenDeposit = () => {
    setTransactionType('deposit');
    setTransactionFormOpen(true);
  };

  // Handler to open withdrawal transaction form
  const handleOpenWithdraw = () => {
    setTransactionType('withdrawal');
    setTransactionFormOpen(true);
  };

  // Handler to open investment form
  const handleOpenInvestmentForm = () => {
    setEditingInvestment(null);
    setInvestmentFormOpen(true);
  };

  // Handler to open edit form with investment data
  const handleEditInvestment = (investment: InvestmentAccount) => {
    setEditingInvestment(investment);
    setInvestmentFormOpen(true);
  };

  // Handler to close investment form and reset editingInvestment
  const handleCancelInvestmentForm = () => {
    setInvestmentFormOpen(false);
    setEditingInvestment(null);
  };

  // Handler to close transaction form
  const handleCancelTransactionForm = () => {
    setTransactionFormOpen(false);
  };

  // Handler for saving transaction (deposit or withdrawal)
  const handleSaveTransaction = async (data: InvestmentTransactionData) => {
    try {
      await createInvestmentTransaction.mutateAsync(data);
      toast.success(`${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`);
      setTransactionFormOpen(false);
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error(`Failed to record ${data.transaction_type === 'deposit' ? 'deposit' : 'withdrawal'}`);
    }
  };

  // Handler for saving investment (create or update)
  const handleSaveInvestment = async (data: InvestmentFormData) => {
    try {
      if (editingInvestment) {
        // Update existing investment
        await updateInvestment.mutateAsync({
          id: editingInvestment.id,
          ...data,
        });
        toast.success('Investment updated successfully');
      } else {
        // Create new investment with Balanced Fund type
        await createInvestment.mutateAsync({
          ...data,
          investment_type_id: balancedFundTypeId || data.investment_type_id,
        });
        toast.success('Investment created successfully');
      }
      setInvestmentFormOpen(false);
      setEditingInvestment(null);
    } catch (error) {
      console.error('Error saving investment:', error);
      toast.error(editingInvestment ? 'Failed to update investment' : 'Failed to create investment');
    }
  };

  // Handler to open delete confirmation dialog
  const handleDeleteInvestment = (investmentId: string) => {
    setDeletingInvestmentId(investmentId);
  };

  // Handler to close delete confirmation dialog
  const handleCancelDelete = () => {
    setDeletingInvestmentId(null);
  };

  // Handler to confirm and execute deletion
  const handleConfirmDelete = async () => {
    if (!deletingInvestmentId) return;

    try {
      await deleteInvestment.mutateAsync(deletingInvestmentId);
      toast.success('Investment deleted successfully');
      setDeletingInvestmentId(null);
    } catch (error) {
      console.error('Error deleting investment:', error);
      toast.error('Failed to delete investment');
    }
  };

  // Check if deposit/withdraw buttons should be disabled
  const hasInvestments = balancedFunds.length > 0;

  // Determine if we're in a loading state
  const isLoading = isLoadingInvestments || isLoadingTypes;

  // Handle investment data loading errors
  useEffect(() => {
    if (investmentsError) {
      toast.error('Failed to load investments. Please try again.');
    }
  }, [investmentsError]);

  // Handle investment types loading errors
  useEffect(() => {
    if (typesError) {
      toast.error('Failed to load investment types. Please try again.');
    }
  }, [typesError]);

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="container mx-auto p-6 space-y-6" aria-busy={isLoading} aria-live="polite">
        {/* Page Header */}
        {isLoading ? (
          <PageHeaderSkeleton />
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Balanced Funds</h1>
              <p className="text-muted-foreground mt-2">
                Manage your balanced fund investments
              </p>
            </div>

            {/* Action Button Group */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full lg:w-auto mt-4 lg:mt-0">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search funds..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {/* Currency Toggle */}
              <CurrencyToggle
                baseCurrency={baseCurrency}
                selectedCurrency={selectedCurrency}
                onCurrencyChange={setSelectedCurrency}
                balance={totalNetWorth}
                showBalance={false}
              />

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto">
                <Button
                  onClick={handleOpenDeposit}
                  disabled={!hasInvestments}
                  aria-label="Deposit funds to balanced fund investment"
                  aria-disabled={!hasInvestments}
                  className="min-h-[44px] min-w-[44px]"
                >
                  Deposit
                </Button>

                <Button
                  onClick={handleOpenWithdraw}
                  disabled={!hasInvestments}
                  aria-label="Withdraw funds from balanced fund investment"
                  aria-disabled={!hasInvestments}
                  className="min-h-[44px] min-w-[44px]"
                >
                  Withdraw
                </Button>

                <Button
                  onClick={handleOpenInvestmentForm}
                  aria-label="Add new balanced fund investment"
                  className="min-h-[44px] min-w-[44px]"
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Currency Conversion Loading Indicator */}
        {isConverting && (
          <Alert role="status" aria-live="polite" aria-busy="true">
            <AlertDescription>
              Converting to {selectedCurrency}...
            </AlertDescription>
          </Alert>
        )}

        {/* Currency Conversion Error */}
        {conversionError && (
          <Alert variant="destructive" role="status" aria-live="polite">
            <AlertDescription>
              Failed to convert currencies. Displaying amounts in original currencies.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary Section - Responsive Grid Layout */}
        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PortfolioSummarySkeleton />
            <DistributionChartSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* TypeSummaryCard Component */}
            <TypeSummaryCard
              typeId={balancedFundTypeId || ''}
              investments={convertedInvestments}
              currency={selectedCurrency}
              isLoading={isLoadingInvestments || isLoadingTypes}
            />

            {/* InvestmentDistributionChart Component */}
            <InvestmentDistributionChart
              investments={convertedInvestments}
              currency={selectedCurrency}
              isLoading={isLoadingInvestments || isLoadingTypes}
            />
          </div>
        )}

        {/* Investment List / Empty State */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <AccountCardSkeleton key={i} />
            ))}
          </div>
        ) : convertedInvestments.length > 0 ? (
          <div 
            role="list" 
            aria-label="Balanced Fund investments"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {convertedInvestments.map((investment) => (
              <InvestmentCard
                key={investment.id}
                investment={investment}
                currency={selectedCurrency}
                onEdit={() => handleEditInvestment(investment)}
                onDelete={() => handleDeleteInvestment(investment.id)}
              />
            ))}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardHeader>
              <CardTitle>No Balanced Fund Investments</CardTitle>
              <CardDescription>
                You haven't added any balanced fund investments yet. Get started by adding your first investment.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleOpenInvestmentForm}
                className="min-h-[44px] min-w-[44px]"
                aria-label="Add your first balanced fund investment"
              >
                Add Your First Investment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Investment Dialog */}
        <Dialog open={investmentFormOpen} onOpenChange={setInvestmentFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="investment-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingInvestment ? 'Edit Investment' : 'Add Balanced Fund Investment'}
              </DialogTitle>
              <DialogDescription id="investment-dialog-description">
                {editingInvestment 
                  ? 'Update the details of your Balanced Fund investment.' 
                  : 'Add a new Balanced Fund investment to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentForm
              investment={editingInvestment}
              preselectedType={balancedFundTypeId}
              investmentTypes={investmentTypes}
              riskRatings={riskRatings}
              riskRatingsError={riskRatingsError}
              onSave={handleSaveInvestment}
              onCancel={handleCancelInvestmentForm}
              isSubmitting={createInvestment.isPending || updateInvestment.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Deposit/Withdraw Transaction Dialog */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit' 
                  ? 'Record a deposit to your Balanced Fund investment.' 
                  : 'Record a withdrawal from your Balanced Fund investment.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentTransactionForm
              transactionType={transactionType}
              investments={balancedFunds}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={handleCancelTransactionForm}
              isSubmitting={createInvestmentTransaction.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingInvestmentId} onOpenChange={(open) => !open && handleCancelDelete()}>
          <AlertDialogContent aria-describedby="delete-warning-message">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Investment</AlertDialogTitle>
              <AlertDialogDescription id="delete-warning-message">
                This action cannot be undone. This will permanently delete the investment account and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
